/**
 * Mobile Wish List Page with Dedicated Filter Bar
 * Displays wishes with collapsible filter panel designed for wishes
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileBottomNav from '../../components/mobile/MobileBottomNav';
import MobileWishCard from '../../components/mobile/MobileWishCard';
import MobileWishFilterBar from '../../components/mobile/MobileWishFilterBar';
import { NotificationCenter } from '../../components/common/NotificationCenter/NotificationCenter';
import { getWishes } from '../../services/wishesApi';
import styles from './MobileWishListPage.module.css';
import type { ApiWish } from '../../types';
import type { Notification } from '../../services/notificationsApi';

const MobileWishListPage = () => {
  const navigate = useNavigate();
  const [wishes, setWishes] = useState<ApiWish[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [filters, setFilters] = useState<{
    category?: string;
    region?: string;
    urgent?: boolean;
    search?: string;
  }>({});
  const [showFilterBar, setShowFilterBar] = useState(false);

  const categories = ['維修', '保養', '美容', '改裝'];
  const regions = ['台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市'];


  useEffect(() => {
    fetchWishes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchWishes = async () => {
    setLoading(true);
    try {
      const result = await getWishes(filters);
      setWishes(result.data);
    } catch (error) {
      console.error('Failed to fetch wishes:', error);
      setWishes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: {
    category?: string;
    region?: string;
    urgent?: boolean;
    search?: string;
  }) => {
    setFilters(newFilters);
    setShowFilterBar(false); // Close panel after applying filters
  };

  const handleSearchToggle = () => {
    setShowFilterBar(!showFilterBar);
  };

  const handleNotificationClick = (notification: Notification) => {
    console.log('Notification clicked:', notification);
  };

  return (
    <div className={styles.container}>
      <MobileHeader
        title="許願池"
        showBack={false}
        showSearch
        onSearch={handleSearchToggle}
        rightAction={<NotificationCenter onNotificationClick={handleNotificationClick} />}
      />

      <div className={styles.content}>
        {/* Filter Bar Panel (Collapsible) */}
        {showFilterBar && (
          <div className={styles.filterPanel}>
            <div className={styles.panelHeader}>
              <h3>篩選條件</h3>
              <button
                className={styles.closePanelBtn}
                onClick={() => setShowFilterBar(false)}
              >
                ✕
              </button>
            </div>
            <MobileWishFilterBar
              onFilterChange={handleFilterChange}
              categories={categories}
              regions={regions}
            />
          </div>
        )}

        {/* Wish List */}
        {loading ? (
          <div className={styles.loading}>載入中...</div>
        ) : (
          <div className={styles.list}>
            <div className={styles.actionBar}>
              <button
                className={styles.createWishBtn}
                onClick={() => navigate('/wishes/new')}
              >
                我要許願
              </button>
            </div>
            {wishes.length === 0 ? (
              <div className={styles.empty}>
                <p>找不到符合條件的許願</p>
                <button
                  className={styles.resetBtn}
                  onClick={() => setFilters({})}
                >
                  重設篩選
                </button>
              </div>
            ) : (
              wishes.map((wish) => (
                <MobileWishCard key={wish.id} wish={wish} />
              ))
            )}
          </div>
        )}
      </div>

      <MobileBottomNav />
    </div>
  );
};

export default MobileWishListPage;
